import { useState } from "react";

import {
  NATO_DIGITS,
  NATO_LETTERS,
  canSpeak,
  speak,
  type PhoneticEntry,
} from "@nav-games/comms";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface NatoReviewProps {
  onNavigate: (to: string) => void;
}

/// Press-to-hear NATO alphabet — no scoring, no card flow. A grid
/// of letter + digit tiles, each one speaks its code word and
/// shows the canonical pronunciation underneath. Useful for ear
/// training outside the flashcards drill.
export function NatoReview({ onNavigate }: NatoReviewProps) {
  const [playingAll, setPlayingAll] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const haveSpeech = canSpeak();

  const playOne = async (entry: PhoneticEntry) => {
    setActive(entry.char);
    await speak(entry.word);
    setActive(null);
  };

  const playAll = async () => {
    setPlayingAll(true);
    const all = [
      ...Object.values(NATO_LETTERS),
      ...Object.values(NATO_DIGITS),
    ];
    for (const e of all) {
      setActive(e.char);
      await speak(e.word);
      // Small pause between entries.
      await new Promise((r) => setTimeout(r, 120));
    }
    setActive(null);
    setPlayingAll(false);
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <h2 style={{ margin: 0 }}>NATO phonetic — audio review</h2>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "nato" : null} onClose={() => setHelpOpen(false)} />
      <p className="muted">
        Tap any tile to hear the code word read aloud. Useful for
        ear training before a long radio watch, or when you can't
        quite remember whether Quebec was "kuh-BECK" or "kweh-BECK"
        (it's the former).
      </p>

      {!haveSpeech && (
        <div
          className="card"
          style={{ borderColor: "var(--warn)", borderWidth: 2 }}
        >
          <p className="small" style={{ margin: 0 }}>
            This device doesn't expose a speech-synthesis voice, so
            the audio buttons won't speak. You can still read the
            pronunciations below.
          </p>
        </div>
      )}

      <div className="card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <h3 style={{ margin: 0 }}>Letters</h3>
          {haveSpeech && (
            <button
              className="secondary"
              onClick={playAll}
              disabled={playingAll}
              style={{ fontSize: 13, padding: "6px 12px" }}
            >
              {playingAll ? "Playing…" : "▶ Play A-Z, 0-9"}
            </button>
          )}
        </div>
        <Grid
          entries={Object.values(NATO_LETTERS)}
          active={active}
          onTap={(e) => void playOne(e)}
          disabled={!haveSpeech || playingAll}
        />
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Digits</h3>
        <Grid
          entries={Object.values(NATO_DIGITS)}
          active={active}
          onTap={(e) => void playOne(e)}
          disabled={!haveSpeech || playingAll}
        />
      </div>

      <button
        className="secondary"
        onClick={() => onNavigate("/play/mayday")}
      >
        ← Back to Mayday
      </button>
    </>
  );
}

function Grid({
  entries,
  active,
  onTap,
  disabled,
}: {
  entries: readonly PhoneticEntry[];
  active: string | null;
  onTap: (e: PhoneticEntry) => void;
  disabled: boolean;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
        gap: 10,
      }}
    >
      {entries.map((e) => {
        const isActive = active === e.char;
        return (
          <button
            key={e.char}
            className="secondary"
            disabled={disabled}
            onClick={() => onTap(e)}
            aria-label={`Speak ${e.word}, pronounced ${e.pronunciation}`}
            style={{
              padding: "12px 8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
              borderColor: isActive ? "var(--accent)" : undefined,
              borderWidth: isActive ? 2 : 1,
              background: isActive ? "var(--accent-soft)" : undefined,
            }}
          >
            <span
              style={{
                fontSize: 28,
                fontWeight: 700,
                letterSpacing: "0.02em",
                lineHeight: 1,
              }}
            >
              {e.char}
            </span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{e.word}</span>
            <span className="small muted" style={{ fontSize: 11 }}>
              {e.pronunciation}
            </span>
          </button>
        );
      })}
    </div>
  );
}
